import moment from "moment-timezone";

/**
 * Calendar-day helpers anchored to the TENANT timezone.
 *
 * Day keys were previously built with `new Date(x).toISOString().split("T")[0]`,
 * which is UTC — nobody's configured timezone. For a tenant on Asia/Calcutta
 * viewed from Mexico, an entry at 2026-10-01T04:00Z was labelled "Oct 1" by
 * UTC, is "Oct 1 09:30" for the tenant and "Sep 30 22:00" locally. Grouping and
 * labelling therefore disagreed with the server, which filters ranges by the
 * tenant zone.
 *
 * Everything user-facing uses the tenant zone so the whole company sees the
 * same day for the same entry, matching how totals and invoice periods are
 * calculated server-side.
 */

/** The tenant timezone (app_settings.timeZone), falling back to UTC. */
export const getTenantZone = () => {
  try {
    return global.config.getTimeZone();
  } catch (error) {
    return "UTC";
  }
};

/**
 * Stable YYYY-MM-DD key for grouping/comparing rows by calendar day.
 * @returns {string|null} null when the input is not a usable date.
 */
export const tenantDayKey = (value) => {
  if (!value) return null;
  const m = moment.tz(value, getTenantZone());
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

/**
 * Human-readable day in the tenant zone, e.g. "October 01, 2026".
 */
export const formatTenantDate = (value, format = "MMMM DD, YYYY") => {
  if (!value) return "";
  const m = moment.tz(value, getTenantZone());
  return m.isValid() ? m.format(format) : "";
};

/**
 * The zone the person using the app is actually in, per their browser.
 *
 * Distinct from getTenantZone(): that is the company-wide zone used for
 * totals, this is where the individual logger sits. An entry's localDate is
 * the day THEY were on, which is what makes a filter return the same rows no
 * matter where the viewer is.
 */
export const getLoggerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    return "UTC";
  }
};

/**
 * "YYYY-MM-DD" for a date as the logger sees it.
 *
 * Deliberately reads the value's own calendar parts rather than converting
 * through a zone: the input is a day picked in a calendar UI, and converting
 * it is what rolled entries onto the wrong day.
 */
export const loggerDayKey = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

/**
 * The zone an individual entry should be rendered in.
 *
 * Entries written since the localDate work carry the zone their logger was in;
 * anything older falls back to the tenant zone, which is how it has always been
 * displayed. Reading an entry's own zone is what keeps its clock time and its
 * `localDate` heading on the same day for every viewer.
 */
export const getEntryZone = (entry) => {
  const zone = entry && entry.localZone;
  if (zone && moment.tz.zone(zone)) return zone;
  return getTenantZone();
};

/**
 * The day an entry belongs to, preferring the stored `localDate`.
 *
 * `entry.date` is an instant, so deriving a day from it re-introduces the
 * viewer/tenant shift that `localDate` exists to remove.
 */
export const getEntryDayKey = (entry) => {
  const stored = entry && entry.localDate;
  if (typeof stored === "string" && /^\d{4}-\d{2}-\d{2}$/.test(stored.trim())) {
    return stored.trim();
  }
  const source = entry && (entry.date || entry.startTime);
  if (!source) return null;
  const m = moment(source).tz(getEntryZone(entry));
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

/**
 * The value the per-entry date picker and the save path both read.
 *
 * Returns a plain "YYYY-MM-DD HH:mm:ss" wall clock in the entry's own zone,
 * with the day pinned to the stored `localDate`. The day has to come from
 * `localDate` rather than from the instant, because that is the day the entry
 * is grouped and headed under — deriving it again from `entry.date` is what
 * made the picker open on Oct 9 while the heading said Oct 8.
 */
export const entryDateEditValue = (entry, zone) => {
  const targetZone = (zone && moment.tz.zone(zone) && zone) || getEntryZone(entry);
  const source = (entry && (entry.startTime || entry.date)) || null;
  const clock = source ? moment.utc(source).tz(targetZone) : null;
  const timePart = clock && clock.isValid() ? clock.format("HH:mm:ss") : "00:00:00";
  const dayPart = getEntryDayKey(entry) || (clock && clock.isValid() ? clock.format("YYYY-MM-DD") : null);
  if (!dayPart) return null;
  return `${dayPart} ${timePart}`;
};

/**
 * Format an entry's own calendar day for display.
 *
 * Takes the day from `localDate` and formats the STRING, never the instant.
 * Running the instant through a zone is what made a Pacific/Pago_Pago entry
 * logged on Oct 11 print as "October 12, 2026" for an Asia/Calcutta tenant —
 * the rows were filtered correctly, only the label disagreed with them.
 */
export const formatEntryDay = (entry, format = "MMMM DD, YYYY") => {
  const dayKey = getEntryDayKey(entry);
  if (!dayKey) return "";
  const m = moment(dayKey, "YYYY-MM-DD", true);
  return m.isValid() ? m.format(format) : "";
};
